import React, { useEffect, useState } from 'react';
import { getRiOriginDetail } from '../../../api/auth';
import DriveBucketBadge from '../primitives/DriveBucketBadge';
import CongestionBadge from '../primitives/CongestionBadge';
import { RiLoading, RiError } from '../primitives/States';
import { useReload } from '../lib/loadState';
import { cleanLocationLabel } from '../lib/labelFormat';
import { fmtUsd } from '../../../utils/format';

const fmt1 = (n) => (n == null ? '—' : Number(n).toFixed(1));

export default function OriginDetailPanel({ clientId, originLat, originLon, range, onClose }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [state, setState] = useState({ loading: true, data: null, failed: false });
  const [reloadKey, retry] = useReload();

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, failed: false });
    getRiOriginDetail(clientId, { originLat, originLon, from, to })
      .then((r) => { if (!cancelled) setState({ loading: false, data: r?.data || null, failed: false }); })
      .catch(() => { if (!cancelled) setState({ loading: false, data: null, failed: true }); });
    return () => { cancelled = true; };
  }, [clientId, originLat, originLon, from, to, reloadKey]);

  const { loading, data, failed } = state;
  const route = data?.route;
  const guests = data?.booked_guests || [];

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <aside className="fixed top-0 right-0 h-full w-[480px] max-w-full bg-white dark:bg-gray-900 shadow-xl z-50 overflow-y-auto border-l border-gray-200 dark:border-gray-800">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Origin</div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {route?.origin_label ? cleanLocationLabel(route.origin_label) : 'Unknown Origin'}
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                ({originLat.toFixed(3)}, {originLon.toFixed(3)})
              </span>
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none p-1">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {loading && <RiLoading />}
          {!loading && failed && <RiError message="Couldn't load this origin's route. The request failed or took too long." onRetry={retry} />}
          {!loading && !failed && !route && (
            <div className="text-sm text-gray-500 dark:text-gray-400 italic">No route data for this origin.</div>
          )}
          {!loading && route && (
            <>
              <div className="flex items-center gap-2">
                <DriveBucketBadge bucket={route.drive_bucket} />
                {route.origin_accuracy && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">accuracy: {route.origin_accuracy}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat label="Drive time" value={route.drive_time_hours != null ? `${fmt1(route.drive_time_hours)} hrs` : '—'} />
                <Stat label="Distance" value={route.drive_distance_miles != null ? `${Math.round(route.drive_distance_miles).toLocaleString()} mi` : '—'} />
                <Stat label="Fuel (round trip)" value={route.fuel_gallons != null ? `${fmt1(route.fuel_gallons)} gal` : '—'} />
                <Stat label="Fuel cost" value={route.fuel_cost_roundtrip != null ? fmtUsd(route.fuel_cost_roundtrip, { cents: true }) : '—'} />
                <div className="col-span-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Congestion</div>
                  <div className="mt-1"><CongestionBadge seconds={route.congestion_delay_seconds} driveTimeSeconds={route.drive_time_seconds} /></div>
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Highway</div>
                <div className="text-sm text-gray-800 dark:text-gray-200">{route.route_summary || '—'}</div>
              </div>

              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Booked Guests From Here
                </div>
                {guests.length === 0 ? (
                  <div className="text-sm italic text-gray-500 dark:text-gray-400">No booked guests in this date range.</div>
                ) : (
                  <div className="space-y-2">
                    {guests.map((g, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {[g.first_name, g.last_name].filter(Boolean).join(' ') || 'Anonymous'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {g.check_in_date} · {g.num_nights ?? '—'} nights · ${g.reservation_value ?? '—'}
                          {g.site_category ? ` · ${g.site_category}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="mt-0.5 text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}
